import type { AddCategory, AddItemFields, AddItemStep } from "@kovernow/flows/add_item";
import { Button } from "@kovernow/react-ui/components/button";
import { useId, type RefObject } from "react";
import { useT } from "../../core/use-t";

/**
 * Server field errors from `POST /items` (i18n keys `errors.<code>`), by
 * field: the legacy details/photos/condition fields, or a collector field key.
 */
export type ServerFieldErrors = Partial<Record<string, string>>;

export interface StepProps {
  fields: AddItemFields;
  setFields: (update: (f: AddItemFields) => AddItemFields) => void;
  goTo: (step: AddItemStep, category?: AddCategory | null, opts?: { replace?: boolean }) => void;
  back: () => void;
  headingRef: RefObject<HTMLHeadingElement | null>;
  serverErrors: ServerFieldErrors;
  clearServerError: (field: string) => void;
}

export const STEP_HEADING_ID = "add-item-step-heading";

/** The step's h2 (focused on every step change). */
export function StepHeading({ headingRef, children }: { headingRef: RefObject<HTMLHeadingElement | null>; children: string }) {
  return (
    <h2 id={STEP_HEADING_ID} ref={headingRef} tabIndex={-1} className="text-xl font-semibold outline-none" data-testid="step-heading">
      {children}
    </h2>
  );
}

/**
 * Back + Continue. Continue is `aria-disabled` while the step is invalid;
 * pressing it then shows every field error (with the visible reason).
 */
export function StepActions({
  onBack,
  onContinue,
  invalid,
  onInvalid,
  continueLabel,
}: {
  onBack?: () => void;
  onContinue: () => void;
  invalid?: boolean;
  onInvalid?: () => void;
  /** Defaults to "Continue". */
  continueLabel?: string;
}) {
  const t = useT();
  const reasonId = useId();
  return (
    <div className="flex flex-col gap-2 sm:flex-row-reverse sm:items-start">
      <div className="flex flex-col gap-1 sm:min-w-48">
        <Button
          type="button"
          variant="cta"
          size="touch"
          data-testid="continue"
          aria-disabled={invalid ? true : undefined}
          aria-describedby={invalid ? reasonId : undefined}
          onClick={() => (invalid ? onInvalid?.() : onContinue())}
        >
          {continueLabel ?? t("addItem.continue")}
        </Button>
        {invalid ? (
          <p id={reasonId} className="text-sm text-muted-foreground">
            {t("addItem.fixErrors")}
          </p>
        ) : null}
      </div>
      {onBack ? (
        <Button type="button" variant="outline" size="touch" onClick={onBack}>
          {t("addItem.back")}
        </Button>
      ) : null}
    </div>
  );
}
